import type { AIAnalysisRequest, AIAnalysisResult, AIClient, StreamingAIServiceState } from '$lib/types/ai';
import { AIStateService } from './AIStateService';
import { AIUtilityService } from './AIUtilityService';

/**
 * AI Streaming Service
 * Manages streaming analysis with progress updates and state management
 * Single Responsibility: Streaming operations and progress tracking
 */
export class AIStreamingService {
  private stateService: AIStateService;

  constructor(stateService: AIStateService) {
    this.stateService = stateService;
  }

  /**
   * Handle streaming analysis with progress updates
   */
  async handleStreamingAnalysis(
    request: AIAnalysisRequest,
    aiClient: AIClient
  ): Promise<AIAnalysisResult[]> {
    return new Promise((resolve, reject) => {
      if (!aiClient.analyzeCorrectionsStreaming) {
        reject(new Error('Streaming not supported by current client'));
        return;
      }

      // Initialize streaming state
      this.initializeStreamingState();

      let allChunks: string[] = [];
      let progressCount = 0;

      const onProgress = (chunk: string) => {
        allChunks.push(chunk);
        progressCount++;
        
        // Estimate progress based on chunk count (rough approximation)
        const estimatedProgress = Math.min((progressCount / 50) * 100, 90);
        
        this.updateStreamingProgress(allChunks, chunk, estimatedProgress);
      };

      const onComplete = (results: AIAnalysisResult[]) => {
        this.completeStreaming(allChunks);
        resolve(results);
      };

      const onError = (error: Error) => {
        // Capture fallback history if client supports it
        if ('getFallbackHistory' in aiClient) {
          const history = (aiClient as any).getFallbackHistory();
          this.stateService.updateState({ fallbackHistory: history });
        }
        
        this.failStreaming(allChunks);
        reject(error);
      };

      // Start streaming analysis
      aiClient.analyzeCorrectionsStreaming(request, onProgress, onComplete, onError);
    });
  }

  /**
   * Initialize streaming state
   */
  private initializeStreamingState(): void {
    this.stateService.updateState({
      streaming: {
        isStreaming: true,
        chunks: [],
        currentChunk: '',
        progress: 0
      }
    });
  }

  /**
   * Update streaming progress
   */
  private updateStreamingProgress(chunks: string[], currentChunk: string, progress: number): void {
    this.stateService.updateState({
      streaming: {
        isStreaming: true,
        chunks: [...chunks],
        currentChunk,
        progress
      }
    });
  }

  /**
   * Complete streaming operation
   */
  private completeStreaming(chunks: string[]): void {
    this.stateService.updateState({
      streaming: {
        isStreaming: false,
        chunks: [...chunks],
        currentChunk: '',
        progress: 100
      }
    });
  }

  /**
   * Handle streaming failure
   */
  private failStreaming(chunks: string[]): void {
    this.stateService.updateState({
      streaming: {
        isStreaming: false,
        chunks: [...chunks],
        currentChunk: '',
        progress: 0
      }
    });
  }

  /**
   * Cancel ongoing streaming analysis
   */
  cancelStreaming(): void {
    const currentState = this.stateService.getState();
    if (currentState.streaming?.isStreaming) {
      this.stateService.updateState({
        isAnalyzing: false,
        streaming: {
          isStreaming: false,
          chunks: currentState.streaming?.chunks || [],
          currentChunk: '',
          progress: 0
        }
      });
    }
  }

  /**
   * Check if currently streaming
   */
  isStreaming(): boolean {
    const state = this.stateService.getState();
    return state.streaming?.isStreaming === true;
  }

  /**
   * Get streaming progress
   */
  getStreamingProgress(): {
    isStreaming: boolean;
    progress: number;
    currentChunk: string;
    totalChunks: number;
  } {
    const state = this.stateService.getState();
    const streaming = state.streaming;
    
    return {
      isStreaming: streaming?.isStreaming ?? false,
      progress: streaming?.progress ?? 0,
      currentChunk: streaming?.currentChunk ?? '',
      totalChunks: streaming?.chunks?.length ?? 0
    };
  }

  /**
   * Get all streaming chunks
   */
  getStreamingChunks(): string[] {
    const state = this.stateService.getState();
    return state.streaming?.chunks ?? [];
  }

  /**
   * Reset streaming state
   */
  resetStreaming(): void {
    this.stateService.updateState({
      streaming: undefined
    });
  }

  /**
   * Enable streaming mode
   */
  enableStreaming(): void {
    this.stateService.updateState({
      streaming: {
        isStreaming: false,
        chunks: [],
        currentChunk: '',
        progress: 0
      }
    });
  }

  /**
   * Stream analysis helper method with fallback
   */
  async streamAnalysis(prompt: string, aiClient: AIClient): Promise<string> {
    if (!aiClient) {
      throw new Error('AI client not initialized');
    }

    // For now, use non-streaming as fallback
    // TODO: Implement actual streaming when client supports it
    return await aiClient.sendMessage(prompt);
  }

  /**
   * Calculate estimated completion time
   */
  getEstimatedTimeRemaining(startTime: number, progress: number): number {
    if (progress <= 0) return 0;
    
    const elapsed = Date.now() - startTime;
    const estimatedTotal = elapsed / (progress / 100);
    return Math.max(0, estimatedTotal - elapsed);
  }

  /**
   * Format streaming status for display
   */
  getStreamingStatus(): {
    status: 'idle' | 'initializing' | 'streaming' | 'completed' | 'error';
    message: string;
    progress: number;
  } {
    const state = this.stateService.getState();
    
    if (!state.streaming) {
      return {
        status: 'idle',
        message: 'Ready to analyze',
        progress: 0
      };
    }
    
    const { isStreaming, progress } = state.streaming;
    
    if (isStreaming) {
      if (progress === 0) {
        return {
          status: 'initializing',
          message: 'Initializing analysis...',
          progress: 0
        };
      } else {
        return {
          status: 'streaming',
          message: `Analyzing... ${Math.round(progress)}%`,
          progress
        };
      }
    } else {
      if (progress === 100) {
        return {
          status: 'completed',
          message: 'Analysis completed',
          progress: 100
        };
      } else {
        return {
          status: 'error',
          message: 'Analysis failed',
          progress: 0
        };
      }
    }
  }
}